import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';

interface HelpTooltipProps {
  text: string;
  isDarkMode: boolean;
  iconSize?: number;
}

const HelpTooltip: React.FC<HelpTooltipProps> = ({ text, isDarkMode, iconSize = 13 }) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const visible = isOpen || isHovering;

  const updateCoords = useCallback(() => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 8, left: rect.left + rect.width / 2 });
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    updateCoords();
    window.addEventListener('scroll', updateCoords, true);
    window.addEventListener('resize', updateCoords);
    return () => {
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
  }, [visible, updateCoords]);

  return (
    <div className="relative inline-flex items-center">
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setIsOpen(prev => !prev); }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        className={`p-1 rounded-full transition-colors focus:outline-none ${
          isDarkMode ? 'text-slate-500 hover:text-blue-400 hover:bg-gray-800' : 'text-slate-400 hover:text-blue-600 hover:bg-slate-100'
        }`}
        title="Aclaración"
        aria-label="Aclaración"
      >
        <HelpCircle size={iconSize} className="stroke-[2.2]" />
      </button>
      {visible && coords && createPortal(
        <div
          className={`fixed -translate-x-1/2 w-64 p-2.5 backdrop-blur-sm text-[11px] leading-snug rounded-xl shadow-xl border z-[999] text-center font-normal transition-opacity duration-150 pointer-events-none ${
            isDarkMode ? 'bg-slate-800/95 text-white border-slate-700' : 'bg-white/95 text-slate-800 border-slate-200'
          }`}
          style={{ top: coords.top, left: coords.left }}
        >
          {text}
        </div>,
        document.body
      )}
    </div>
  );
};

export default HelpTooltip;
